import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Users, Search, Trash, UserMinus, UserPlus } from 'lucide-react';
import { toast } from 'react-toastify';
import useOrganizationStore from '@/stores/SuperAdminStore/OrganizationStore';
import { deleteUser } from '@/services/PagesServices/SuperAdminUsersService';
import { LoadingSpinner } from '@/Components/common';
import './OrganizationDetails.css';

interface OrganizationUser {
  id: string;
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  roleId: number;
  assignedAt: string;
}

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  confirmButtonClass: string;
  onConfirm: () => void;
  onCancel: () => void;
  isProcessing: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  confirmButtonClass,
  onConfirm,
  onCancel,
  isProcessing,
}) => {
  if (!isOpen) return null;

  return (
    <div className="view-org-modal-overlay">
      <div className="view-org-modal-content">
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="view-org-modal-actions">
          <button
            className="view-org-modal-btn view-org-cancel-btn"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            className={`view-org-modal-btn ${confirmButtonClass}`}
            onClick={onConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const OrganizationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getOrganizationById, getOrganizationUsers, removeUser } = useOrganizationStore();

  const [organization, setOrganization] = useState<any>(null);
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Modal states
  const [removeModal, setRemoveModal] = useState<{
    isOpen: boolean;
    user: OrganizationUser | null;
  }>({ isOpen: false, user: null });

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    user: OrganizationUser | null;
  }>({ isOpen: false, user: null });


  useEffect(() => {
    if (id) {
      fetchOrganizationData();
    }
  }, [id]);

  // Debounce search term (1100ms delay)
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
    }, 1100);

    // Cleanup on unmount or when searchTerm changes
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  // Fetch users when page, limit, or debounced search changes
  useEffect(() => {
    if (id && !loading) {
      fetchUsers();
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm]);

  const fetchOrganizationData = async () => {
    if (!id) return;

    try {
      setLoading(true);

      const orgResult = await getOrganizationById(parseInt(id));
      if (orgResult) {
        setOrganization(orgResult);
      } else {
        toast.error('Organization not found');
        navigate('/superadmin/organizations');
        return;
      }

      await fetchUsers();
    } catch (error: any) {
      console.error('Error fetching organization data:', error);
      toast.error(error.message || 'Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!id) return;

    try {
      setUsersLoading(true);

      const usersResult = await getOrganizationUsers(parseInt(id), {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm, // Send search term to backend
      });

      if (usersResult.success) {
        const usersList = usersResult.users || [];
        setUsers(usersList); // Backend already filtered, no need for client-side filtering
        setTotalUsers(usersResult.pagination?.total || usersList.length);
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    // Debouncing will handle resetting to page 1
  };

  const handleRemoveFromOrg = (user: OrganizationUser) => {
    setRemoveModal({ isOpen: true, user });
  };

  const handleDeleteUser = (user: OrganizationUser) => {
    setDeleteModal({ isOpen: true, user });
  };

  const confirmRemoveFromOrg = async () => {
    if (!removeModal.user || !id) return;

    try {
      setProcessingUserId(removeModal.user.id);

      const result = await removeUser(removeModal.user.uuid, parseInt(id));

      if (result.success) {
        toast.success('User removed from organization successfully');
        setRemoveModal({ isOpen: false, user: null });
        await fetchUsers(); // Refresh user list
      } else {
        toast.error(result.message || 'Failed to remove user from organization');
      }
    } catch (error: any) {
      console.error('Error removing user:', error);
      toast.error('Failed to remove user from organization');
    } finally {
      setProcessingUserId(null);
    }
  };

  const confirmDeleteUser = async () => {
    if (!deleteModal.user) return;

    try {
      setProcessingUserId(deleteModal.user.id);

      const result = await deleteUser(deleteModal.user.uuid);

      if (result.success) {
        toast.success('User deleted successfully');
        setDeleteModal({ isOpen: false, user: null });
        await fetchUsers(); // Refresh user list
      } else {
        toast.error(result.message || 'Failed to delete user');
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleOpenAddUserModal = () => {
    // Navigate to add users page
    if (organization && id) {
      navigate(`/superadmin/organizations/${id}/add-users/${encodeURIComponent(organization.name)}`);
    }
  };


  const getRoleBadgeClass = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
      case 'super admin':
        return 'view-org-role-badge-admin';
      case 'instructor':
        return 'view-org-role-badge-instructor';
      case 'student':
      case 'learner':
        return 'view-org-role-badge-student';
      default:
        return 'view-org-role-badge-default';
    }
  };

  const getRoleLabel = (role: string) => {
    if (role.toLowerCase() === 'student') return 'Learner';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const totalPages = Math.ceil(totalUsers / itemsPerPage);

  if (loading) {
    return (
      <div className="view-org-loading">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="view-org-empty">
        <Building2 size={48} color="#94a3b8" />
        <p>Organization not found</p>
        <button onClick={() => navigate('/superadmin/organizations')} className="view-org-back-btn">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="view-org-container">
      {/* Header */}
      <div className="view-org-header">
        <button className="view-org-back-button" onClick={() => navigate('/superadmin/organizations')}>
          <ArrowLeft size={20} />
          Back to Organizations
        </button>
      </div>

      {/* Organization Info Card */}
      <div className="view-org-info-card">
        <div className="view-org-info-header">
          <div className="view-org-icon">
            <Building2 size={32} />
          </div>
          <div className="view-org-info-text">
            <h1>{organization.name}</h1>
            <div className="view-org-meta">
              <span className={`view-org-status-badge ${organization.isActive ? 'active' : 'inactive'}`}>
                {organization.isActive ? 'Active' : 'Inactive'}
              </span>
              <span className="view-org-user-count">
                <Users size={16} />
                {totalUsers} {totalUsers === 1 ? 'User' : 'Users'}
              </span>
              {organization.createdAt && (
                <span className="view-org-created-date">
                  Created: {new Date(organization.createdAt).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Users Table Section */}
      <div className="view-org-users-section">
        <div className="view-org-section-header">
          <div className="view-org-section-title">
            <h2>
              <Users size={24} />
              Organization Users
            </h2>
            <p>{totalUsers} {totalUsers === 1 ? 'user' : 'users'} in this organization</p>
          </div>

          <div className="view-org-header-actions">
            {/* Add User Button */}
            <button
              className="view-org-add-user-btn"
              onClick={handleOpenAddUserModal}
              title="Add user to organization"
            >
              <UserPlus size={18} />
              Add User
            </button>

            {/* Search Bar */}
            <div className="view-org-search-container">
              <Search className="view-org-search-icon" size={18} />
              <input
                type="text"
                placeholder="Search by name, email, or role..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="view-org-search-input"
              />
            </div>
          </div>
        </div>

        {usersLoading ? (
          <div className="view-org-users-loading">
            <LoadingSpinner size="md" />
          </div>
        ) : users.length === 0 ? (
          <div className="view-org-no-users">
            <Users size={48} color="#94a3b8" />
            <p>{searchTerm ? 'No users found matching your search' : 'No users assigned to this organization yet'}</p>
          </div>
        ) : (
          <>
            {/* Users Table */}
            <div className="view-org-table-container">
              <table className="view-org-users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Added On</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="view-org-user-name-cell">
                          <div className="view-org-user-avatar-small">
                            {user.firstName?.[0] || user.email[0].toUpperCase()}
                          </div>
                          <span className="view-org-user-full-name">
                            {user.firstName && user.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user.email}
                          </span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`view-org-role-badge ${getRoleBadgeClass(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td>{formatDate(user.assignedAt)}</td>
                      <td>
                        <div className="view-org-action-buttons">
                          <button
                            className="view-org-action-btn view-org-remove-btn"
                            onClick={() => handleRemoveFromOrg(user)}
                            disabled={processingUserId === user.id}
                            title="Remove from organization"
                          >
                            <UserMinus size={18} />
                          </button>
                          <button
                            className="view-org-action-btn view-org-delete-user-btn"
                            onClick={() => handleDeleteUser(user)}
                            disabled={processingUserId === user.id}
                            title="Delete user"
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="view-org-pagination-container">
                <div className="view-org-pagination-info">
                  <span>Showing</span>
                  <select
                    className="view-org-pagination-select"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span>of {totalUsers}</span>
                </div>

                <div className="view-org-pagination-buttons">
                  <button
                    className="view-org-page-btn"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum = i + 1;
                    if (currentPage > 3 && totalPages > 5) {
                      pageNum = currentPage - 2 + i;
                      if (pageNum > totalPages) pageNum = totalPages - (4 - i);
                    }
                    if (pageNum < 1 || pageNum > totalPages) return null;
                    return (
                      <button
                        key={pageNum}
                        className={`view-org-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    className="view-org-page-btn"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <ConfirmModal
        isOpen={removeModal.isOpen}
        title="Remove User from Organization"
        message={`Are you sure you want to remove ${removeModal.user?.firstName} ${removeModal.user?.lastName} from this organization? They will no longer have access to organization resources.`}
        confirmText="Remove"
        confirmButtonClass="view-org-remove-confirm-btn"
        onConfirm={confirmRemoveFromOrg}
        onCancel={() => setRemoveModal({ isOpen: false, user: null })}
        isProcessing={processingUserId === removeModal.user?.id}
      />

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        title="Delete User"
        message={`Are you sure you want to permanently delete ${deleteModal.user?.firstName} ${deleteModal.user?.lastName}? This action cannot be undone and will remove the user from the entire application.`}
        confirmText="Delete"
        confirmButtonClass="view-org-delete-confirm-btn"
        onConfirm={confirmDeleteUser}
        onCancel={() => setDeleteModal({ isOpen: false, user: null })}
        isProcessing={processingUserId === deleteModal.user?.id}
      />
    </div>
  );
};

export default OrganizationDetails;
